import { Button, Form, InputNumber, Modal, Slider } from "antd";
import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { applyForLoan } from "../../../ApiServices/Apifun";
import { dispatchtoast } from "../../../Utils";
import ReportDrawer from "../../../Common/ReportDrawer";
import Content from "../../../Common/ReceiptContent";

const LoanCalculator = () => {
  const location = useLocation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const planDetails = location.state?.planDetails;
  const minTenureMonths = planDetails?.min_emi_months;
  const maxTenureMonths = planDetails?.max_emi_months;
  const [principal, setPrincipal] = useState(planDetails?.min_amount);
  const [rate, setRate] = useState(7);
  const [tenure, setTenure] = useState(minTenureMonths);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  const [PaybillResult, setPaybillResult] = useState({
    data: null,
    transaction_id: null,
  });
  const [openReceiptDrawer, setOpenReceiptDrawer] = useState(false);

  useEffect(() => {
    if (planDetails) {
      setRate(planDetails?.interest_rate);
      setTenure(minTenureMonths);
    }
  }, [planDetails]);

  const emiDetails = useMemo(() => {
    const P = Number(principal);
    const annualRate = Number(rate);
    const n = Number(tenure);

    const r = annualRate / 12 / 100; // monthly interest rate

    if (!P || !r || !n) return { emi: 0, totalPayment: 0, totalInterest: 0 };

    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

    const totalPayment = emi * n;
    const totalInterest = totalPayment - P;

    return {
      emi: emi.toFixed(2),
      totalPayment: totalPayment.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
    };
  }, [principal, rate, tenure]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 2,
    }).format(value);
  };

  const handleApplyForLoan = () => {
    setBtnLoading(true);
    const formdata = new FormData();
    formdata.append("loan_amount", principal);
    formdata.append("tenure", tenure);
    formdata.append("plan_id", planDetails?.id);
    applyForLoan(formdata)
      .then((res) => {
        console.log("FULL RESPONSE:", res.msg);

        if (res.status) {
          console.log(res.msg);
          dispatchtoast(res.msg);
          setApplyModalOpen(false);
          setBtnLoading(false);
          navigate("/loanRequest");
        } else {
          console.log(res.msg);

          setApplyModalOpen(true);
          dispatchtoast(res.msg, true);
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setBtnLoading(false));
  };

  useEffect(() => {
    if (applyModalOpen) {
      form.setFieldsValue({
        amount: principal,
        tenure: tenure,
      });
    }
  }, [applyModalOpen, principal, tenure, form]);
  return (
    <div className="bg-white shadow-xl rounded-2xl p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
        {planDetails?.plan_name || "EMI"} Calculator
      </h2>

      <div className="mb-4">
        <label className="text-gray-500 text-sm">Total Loan Amount</label>
        <div className="mt-4">
          <Slider
            min={Number(planDetails.min_amount)}
            max={Number(planDetails.max_amount)}
            value={principal}
            onChange={(value) => setPrincipal(value)}
            tooltip={{
              formatter: (value) => `₹ ${value.toLocaleString("en-IN")}`,
            }}
          />
        </div>

        <div className="text-right text-sm text-gray-600 mt-1">
          ₹ {principal.toLocaleString("en-IN")}
        </div>
        {/* <input
          type="number"
          min={planDetails.min_amount}
          max={planDetails.max_amount}
          value={principal}
          onChange={(e) => setPrincipal(e.target.value)}
          className="w-full h-14 mt-1 px-4 text-2xl font-semibold rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary outline-none"
        /> */}
        {/* <InputNumber
          min={planDetails.min_amount}
          max={planDetails.max_amount}
          value={principal}
          onChange={(value) => setPrincipal(value)}
          className="w-full h-14 mt-1 px-4 text-2xl font-semibold rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary outline-none"
        /> */}
      </div>

      <div className="grid grid-cols-1  gap-4">
        {/* <div className="mb-4">
          <label className="text-gray-500 text-sm">Interest Rate (%)</label>
          <input
            type="number"
            min="1"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            className="w-full h-12 mt-1 px-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary outline-none"
          />
        </div> */}
        <div className="mb-6">
          <label className="text-gray-500 text-sm">Tenure (Months)</label>

          <div className="mt-4">
            <Slider
              min={Number(minTenureMonths)}
              max={Number(maxTenureMonths)}
              value={tenure}
              onChange={(value) => setTenure(value)}
              tooltip={{
                formatter: (value) => `${value} Months`,
              }}
            />
          </div>

          <div className="text-right text-sm text-gray-600 mt-1">
            {tenure} Months
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-5 space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-500">Loan Amount</span>
          <span className="font-semibold">₹ {formatCurrency(principal)}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-500">Monthly EMI</span>
          <span className="font-semibold text-green-600">
            ₹ {formatCurrency(emiDetails.emi)}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-500">Total Interest</span>
          <span className="font-semibold text-red-500">
            ₹ {formatCurrency(emiDetails.totalInterest)}
          </span>
        </div>

        <div className="flex justify-between text-lg">
          <span className="font-semibold text-gray-700">Total Payment</span>
          <span className="font-bold text-primary">
            ₹ {formatCurrency(emiDetails.totalPayment)}
          </span>
        </div>

        <button
          onClick={() => setApplyModalOpen(true)}
          className="mt-4 w-full bg-primary text-white py-3 rounded-xl font-semibold hover:bg-black transition-all duration-300 shadow-md hover:shadow-lg"
        >
          Apply
        </button>
      </div>
      {applyModalOpen && (
        <Modal
          open={applyModalOpen}
          onCancel={() => setApplyModalOpen(false)}
          footer={null}
        >
          <h2 className="text-xl font-semibold mb-4">Apply for Loan</h2>

          <Form
            form={form}
            layout="vertical"
            // initialValues={{
            //   amount: principal,
            //   tenure: tenure,
            // }}
            onFinish={handleApplyForLoan}
          >
            <Form.Item
              label="Amount"
              name="amount"
              rules={[
                {
                  required: true,
                  message: "Please input the investment amount!",
                },
                // {
                //   min: planDetails.min_amount,
                //   message: `Minimum amount is ₹ ${formatCurrency(planDetails.min_amount)}`,
                // },
                // {
                //   max: planDetails.max_amount,
                //   message: `Maximum amount is ₹ ${formatCurrency(planDetails.max_amount)}`,
                // },
              ]}
            >
              <input
                disabled
                type="number"
                min={planDetails.min_amount}
                max={planDetails.max_amount}
                value={principal}
                onChange={(e) => setPrincipal(e.target.value)}
                className="w-full mt-1 px-4 text-2xl text-gray-500 font-semibold bg-gray-200 rounded-xl border border-none focus:ring-2 focus:ring-primary outline-none"
              />
            </Form.Item>

            <Form.Item
              label="Tenure (Months)"
              name="tenure"
              rules={[
                { required: true, message: "Please input the tenure!" },
                // {
                //   min: minTenureMonths,
                //   message: `Minimum tenure is ${minTenureMonths} months`,
                // },
                // {
                //   max: maxTenureMonths,
                //   message: `Maximum tenure is ${maxTenureMonths} months`,
                // },
              ]}
            >
              <input
                disabled
                type="number"
                min={minTenureMonths}
                max={maxTenureMonths}
                value={tenure}
                onChange={(e) => setTenure(e.target.value)}
                className="w-full mt-1 px-4 text-2xl text-gray-500  font-semibold bg-gray-200 rounded-xl border border-none focus:ring-2 focus:ring-primary outline-none"
              />
            </Form.Item>
            <Form.Item>
              <button
                onLoad={btnLoading}
                disabled={!form.isFieldsTouched(true)}
                onClick={() => setApplyModalOpen(true)}
                className="mt-4 w-full bg-primary text-white py-3 rounded-xl font-semibold hover:bg-black transition-all duration-300 shadow-md hover:shadow-lg"
              >
                {btnLoading ? "Processing..." : "Apply"}
              </button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {openReceiptDrawer && (
        <ReportDrawer
          title={"Receipt"}
          open={openReceiptDrawer}
          setOpen={setOpenReceiptDrawer}
          content={<Content data={PaybillResult} />}
        />
      )}
    </div>
  );
};

export default LoanCalculator;
